import { FC } from 'react';
import { useNavigate, useRouteError } from 'react-router';
import styled from 'styled-components';

import { APP_ROUTES } from '@navigation/types';
import SkewedButton from '@components/buttons/SkewedButton';
import { DefaultText, PremierText } from '@components/text/Text';
import { errorMessages } from '@utils/constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 20px;
  text-align: center;
  background-color: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
`;

const Title = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize['4xl']};
  margin-bottom: 16px;
`;

const Message = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.lg};
  margin-bottom: 24px;
`;

type RouterError = {
  statusText?: string;
  message?: string;
  data?: string;
  status?: number;
};

type Props = {
  error?: Error | RouterError;
  resetErrorBoundary?: () => void;
};

const ErrorPage: FC<Props> = ({ error, resetErrorBoundary }) => {
  const navigate = useNavigate();
  const routeError = useRouteError();

  const errorToDisplay = error || routeError;

  const isNotFound =
    (errorToDisplay as RouterError)?.status === 404 ||
    (errorToDisplay as RouterError)?.statusText?.includes('Not Found');

  const handleRetry = () => {
    navigate(APP_ROUTES.HOME);
    resetErrorBoundary?.();
  };

  return (
    <Container>
      <Title>
        {isNotFound ? errorMessages.notFound : errorMessages.general}
      </Title>

      {isNotFound && (
        <Message>The page you're looking for doesn't exist</Message>
      )}

      <SkewedButton onClick={handleRetry}>Go to Home</SkewedButton>
    </Container>
  );
};

export default ErrorPage;
